import React, { useState, createContext, useContext } from 'react';
type Language = 'en' | 'es';
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  hasSelectedLanguage: boolean;
  completeSelection: () => void;
  pendingLanguage: Language | null;
  requestLanguageChange: (lang: Language) => void;
  confirmLanguageChange: () => void;
}
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  // Always start with English default, but require selection
  const [language, setLanguageState] = useState<Language>('en');
  const [hasSelectedLanguage, setHasSelectedLanguage] = useState(false);
  const [pendingLanguage, setPendingLanguage] = useState<Language | null>(null);
  // We no longer read from localStorage on mount so the selector always shows
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    // We can still save it for internal route changes if needed, but we don't auto-skip on load
    localStorage.setItem('krly_language', lang);
  };
  const completeSelection = () => {
    setHasSelectedLanguage(true);
  };
  const requestLanguageChange = (lang: Language) => {
    if (lang !== language) {
      setPendingLanguage(lang);
    }
  };
  const confirmLanguageChange = () => {
    if (pendingLanguage) {
      setLanguage(pendingLanguage);
      setPendingLanguage(null);
    }
  };
  return <LanguageContext.Provider value={{
    language,
    setLanguage,
    hasSelectedLanguage,
    completeSelection,
    pendingLanguage,
    requestLanguageChange,
    confirmLanguageChange
  }} data-id="element-227">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}